import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, LoadingState, StatePanel, V2Button } from '@sakhtar/design-system/react';

/**
 * Frames a sales document that the API renders (پیش‌فاکتور / فاکتور): a standalone RTL HTML page
 * that carries the tenant logo, the Persian font and the seal/signature slots. This component only
 * fetches it, shows it in a sandboxed iframe (no scripts) and offers print / save-as-HTML, so the
 * printed page, the saved file and what a user previews are one and the same document.
 */
type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly code: number }
  | { readonly status: 'ready'; readonly html: string };

export function SalesDocumentFrame({
  slug,
  documentUrl,
  backHref,
  backLabel,
  frameTitle,
  downloadName,
}: {
  readonly slug: string;
  /** API path of the document, without the `?download=1` flag. */
  readonly documentUrl: string;
  readonly backHref: string;
  readonly backLabel: string;
  readonly frameTitle: string;
  readonly downloadName: string;
}) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetch(documentUrl, { credentials: 'include', headers: { accept: 'text/html' } })
      .then(async (response) => {
        if (cancelled) return;
        if (response.status === 401) {
          window.location.href = `/t/${slug}/login`;
          return;
        }
        if (!response.ok) {
          setState({ status: 'error', code: response.status });
          return;
        }
        setState({ status: 'ready', html: await response.text() });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', code: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, documentUrl]);

  return (
    <div
      dir="rtl"
      style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, minHeight: '100vh' }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <Link to={backHref} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="chevronRight" size="sm" />
          {backLabel}
        </Link>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            className="sk-btn sk-btn--secondary"
            href={`${documentUrl}?download=1`}
            download={downloadName}
          >
            دانلود HTML
          </a>
          <V2Button
            variant="primary"
            icon="invoice"
            disabled={state.status !== 'ready'}
            onClick={() => frame.current?.contentWindow?.print()}
          >
            چاپ
          </V2Button>
        </div>
      </div>
      {state.status === 'loading' ? <LoadingState /> : null}
      {state.status === 'error' ? (
        <StatePanel
          variant={
            state.code === 403
              ? 'permission_denied'
              : state.code === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            state.code === 403
              ? 'دسترسی ندارید'
              : state.code === 404
                ? 'پیدا نشد'
                : 'قابل نمایش نیست'
          }
        />
      ) : null}
      {state.status === 'ready' ? (
        <iframe
          ref={frame}
          title={frameTitle}
          srcDoc={state.html}
          sandbox="allow-modals allow-same-origin"
          style={{ flex: 1, minHeight: '85vh', width: '100%', border: 0, background: '#e9edf3' }}
        />
      ) : null}
    </div>
  );
}
